import React, { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import { ProductVideoPlayer } from '@/components/live/ProductVideoPlayer';

// PRODUCT-DETAIL-LIVE-VIDEO-1 · 일반 상품 상세 LIVE 소개영상 · 메타 1회 조회 · URL 은 클릭 시에만 · 플레이어 1개 공유.
const secLabel = (s) => (s ? `${Math.round(s)}초` : '');

export const useProductLiveVideo = (productId) => {
  const [meta, setMeta] = useState(null);
  const [item, setItem] = useState(null);
  const busy = useRef(false);
  useEffect(() => {
    setMeta(null); setItem(null);
    let off = false;
    api.get(`/public/products/${productId}/live-video`).then(({ data }) => { if (!off) setMeta(data.video || null); })
      .catch(() => { if (!off) setMeta(null); });
    return () => { off = true; };
  }, [productId]);
  const open = async (product) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.get(`/public/products/${productId}/live-video/play-url`);
      setItem({ url: data.url, duration_sec: data.duration_sec, product });
    } catch { toast.error('영상을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.'); } finally { busy.current = false; }
  };
  return { meta, item, open, close: () => setItem(null) };
};

export const LiveVideoThumb = ({ meta, image, onOpen }) => (
  <button type="button" onClick={onOpen} className="group mt-2 flex items-center gap-3 w-full sm:w-auto p-1.5 pr-4 rounded-xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30]/60 transition-colors" data-testid="product-live-video-thumb">
    <span className="relative w-12 h-16 rounded-lg overflow-hidden bg-black shrink-0">
      {image && <SafeImg src={image} kind="product" alt="" className="w-full h-full object-cover opacity-70" />}
      <span className="absolute inset-0 flex items-center justify-center"><Play size={16} className="text-white fill-white group-hover:scale-110 transition-transform" /></span>
    </span>
    <span className="text-left">
      <span className="block text-xs font-black text-[#FF3B30]">LIVE 소개영상</span>
      <span className="block text-[11px] text-gray-500" data-testid="product-live-video-thumb-duration">판매자가 방송에서 직접 소개 · {secLabel(meta.duration_sec)}</span>
    </span>
  </button>
);

export const LiveVideoPreview = ({ meta, image, onOpen }) => (
  <section className="mt-10" data-testid="product-live-video-preview">
    <h2 className="text-sm font-black tracking-widest uppercase text-gray-500 mb-3">LIVE에서 직접 소개한 상품</h2>
    <button type="button" onClick={onOpen} className="group relative block w-full max-w-[min(280px,33.75vh)] aspect-[9/16] mx-auto sm:mx-0 rounded-2xl overflow-hidden bg-black" data-testid="product-live-video-preview-btn">
      {image && <SafeImg src={image} kind="product" alt="" className="w-full h-full object-cover opacity-60" />}
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
        <span className="w-14 h-14 rounded-full bg-[#FF3B30] flex items-center justify-center group-hover:scale-110 transition-transform"><Play size={22} className="fill-white ml-0.5" /></span>
        <span className="text-sm font-black">라이브 소개영상 보기</span>
        <span className="text-[11px] opacity-80">{secLabel(meta.duration_sec)}</span>
      </span>
    </button>
  </section>
);

export const LiveVideoHost = ({ item, onClose, onBuy }) => {
  const bounds = useRef(null);
  if (!item) return null;
  return (
    <div ref={bounds} className="fixed inset-0 z-[60] pointer-events-none [&>*]:pointer-events-auto" data-testid="product-live-video-host">
      <ProductVideoPlayer item={item} boundsRef={bounds} onClose={onClose} onBuy={() => { onClose(); onBuy(); }} />
    </div>
  );
};
